import { Button } from "@app/ui";
import { useState } from "react";
import { TimeZoneField } from "../../features/task-form/TimeZoneField.js";
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, type I18nKey } from "../../lib/language.js";
import { useI18n } from "../../lib/useI18n.js";
import { toast, useSettingsStore } from "../../stores/index.js";
import type { DefaultView } from "../../stores/settings.store.js";
import { MultiDayFocusPicker } from "./MultiDayFocusPicker.js";
import { SettingsCard } from "./SettingsCard.js";
import { SettingsSection } from "./SettingsSection.js";

const VIEWS: { value: DefaultView; labelKey: I18nKey }[] = [
  { value: "today", labelKey: "common.today" },
  { value: "calendar/month", labelKey: "common.month" },
  { value: "calendar/week", labelKey: "common.week" },
  { value: "calendar/day", labelKey: "common.day" },
  { value: "tasks", labelKey: "common.allTasks" },
];

/** "General" — language, time & region formatting, startup and the reset switch. */
export function GeneralSettings() {
  const { t } = useI18n();
  const timeFormat = useSettingsStore((s) => s.timeFormat);
  const setTimeFormat = useSettingsStore((s) => s.setTimeFormat);
  const defaultView = useSettingsStore((s) => s.defaultView);
  const setDefaultView = useSettingsStore((s) => s.setDefaultView);
  const language = useSettingsStore((s) => s.language);
  const setLanguage = useSettingsStore((s) => s.setLanguage);
  const weekStartsOn = useSettingsStore((s) => s.weekStartsOn);
  const setWeekStartsOn = useSettingsStore((s) => s.setWeekStartsOn);
  const multiDayFocus = useSettingsStore((s) => s.multiDayFocus);
  const setMultiDayFocus = useSettingsStore((s) => s.setMultiDayFocus);
  const defaultTz = useSettingsStore((s) => s.defaultTz);
  const setDefaultTz = useSettingsStore((s) => s.setDefaultTz);
  const resetPreferences = useSettingsStore((s) => s.resetPreferences);
  const setOnboardingComplete = useSettingsStore((s) => s.setOnboardingComplete);
  const setOnboardingStep = useSettingsStore((s) => s.setOnboardingStep);

  const [confirmReset, setConfirmReset] = useState(false);

  return (
    <div className="flex flex-col gap-8">
      <SettingsSection title={t("general.languageTime")}>
        <SettingsCard
          id="language"
          title={t("common.language")}
          description={t("general.language.description")}
          onReset={() => void setLanguage(DEFAULT_LANGUAGE)}
        >
          <div className="flex flex-wrap gap-2">
            {SUPPORTED_LANGUAGES.map((item) => (
              <Button
                key={item.value}
                variant={language === item.value ? "primary" : "outline"}
                onClick={() => void setLanguage(item.value)}
                title={item.label}
              >
                {item.nativeName}
              </Button>
            ))}
          </div>
        </SettingsCard>

        <SettingsCard id="time-format" title={t("general.timeFormat")} onReset={() => void setTimeFormat("24h")}>
          <div className="flex gap-2">
            <Button variant={timeFormat === "24h" ? "primary" : "outline"} onClick={() => void setTimeFormat("24h")}>{t("general.timeFormat.24")}</Button>
            <Button variant={timeFormat === "12h" ? "primary" : "outline"} onClick={() => void setTimeFormat("12h")}>{t("general.timeFormat.12")}</Button>
          </div>
        </SettingsCard>

        <SettingsCard id="week-start" title={t("defaults.weekStart")} onReset={() => void setWeekStartsOn(1)}>
          <div className="flex gap-2">
            <Button variant={weekStartsOn === 0 ? "primary" : "outline"} onClick={() => void setWeekStartsOn(0)}>{t("defaults.sunday")}</Button>
            <Button variant={weekStartsOn === 1 ? "primary" : "outline"} onClick={() => void setWeekStartsOn(1)}>{t("defaults.monday")}</Button>
          </div>
        </SettingsCard>

        <SettingsCard
          id="multi-day-focus"
          title={t("general.multiDayFocus")}
          description={t("general.multiDayFocus.description")}
          onReset={() => void setMultiDayFocus("current_week")}
        >
          <MultiDayFocusPicker
            value={multiDayFocus}
            weekStartsOn={weekStartsOn}
            onChange={(next) => void setMultiDayFocus(next)}
          />
        </SettingsCard>

        <SettingsCard id="default-tz" title={t("defaults.defaultTz")} description={t("defaults.defaultTz.description")}>
          <TimeZoneField value={defaultTz} onChange={(tz) => void setDefaultTz(tz)} />
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("general.startup")}>
        <SettingsCard id="default-view" title={t("general.defaultView")} description={t("general.defaultView.description")} onReset={() => void setDefaultView("today")}>
          <div className="flex flex-wrap gap-2">
            {VIEWS.map((v) => (
              <Button key={v.value} variant={defaultView === v.value ? "primary" : "outline"} onClick={() => void setDefaultView(v.value)}>
                {t(v.labelKey)}
              </Button>
            ))}
          </div>
        </SettingsCard>
      </SettingsSection>

      <SettingsSection title={t("general.reset")}>
        <SettingsCard
          id="reset-prefs"
          title={t("general.resetPrefs")}
          description={t("general.resetPrefs.description")}
        >
          {confirmReset ? (
            <div className="flex items-center gap-2">
              <Button
                variant="destructive"
                onClick={() => { setConfirmReset(false); void resetPreferences().then(() => toast.success(t("general.resetPrefs.done"))); }}
              >
                {t("general.resetPrefs.confirm")}
              </Button>
              <Button variant="ghost" onClick={() => setConfirmReset(false)}>{t("common.cancel")}</Button>
            </div>
          ) : (
            <Button variant="outline" onClick={() => setConfirmReset(true)}>{t("general.resetPrefs.button")}</Button>
          )}
        </SettingsCard>

        <SettingsCard
          id="show-onboarding"
          title={t("general.showOnboarding")}
          description={t("general.showOnboarding.description")}
        >
          <Button
            variant="outline"
            onClick={() => { void setOnboardingStep(null); void setOnboardingComplete(false); }}
          >
            {t("general.showOnboarding.button")}
          </Button>
        </SettingsCard>
      </SettingsSection>
    </div>
  );
}
